import type { ReactNode } from "react"

import { cn as cx } from "@/lib/utils"

// Shared scaffolding for The Lists Desk (/design).
//
// A "plate" is one framed specimen: a paper-white figure on the cream
// canvas. Each plate is treated as its own view for the Highlighter
// Rule: one yellow moment per plate, never per page.

interface ManualSectionProps {
  id: string
  title: string
  lede?: ReactNode
  children: ReactNode
  className?: string
}

export function ManualSection({
  id,
  title,
  lede,
  children,
  className,
}: ManualSectionProps) {
  return (
    <section
      id={id}
      aria-labelledby={`${id}-title`}
      className={cx("scroll-mt-20 py-14 lg:py-20", className)}
    >
      <div className="max-w-container mx-auto flex flex-col gap-8 px-4 lg:gap-10 lg:px-8">
        <header className="flex max-w-3xl flex-col gap-3">
          {/* Scotch rule: the section signature, in place of a per-section
              eyebrow (deliberately omitted; the rule does that job better). */}
          <span aria-hidden="true" className="scotch-rule mb-2" />
          <h2
            id={`${id}-title`}
            className="text-display-xs lg:text-display-sm text-primary font-semibold text-balance"
          >
            {title}
          </h2>
          {lede && <p className="text-secondary text-md max-w-prose">{lede}</p>}
        </header>
        {children}
      </div>
    </section>
  )
}

interface PlateProps {
  title: string
  importPath?: string
  note?: ReactNode
  children: ReactNode
  className?: string
  bodyClassName?: string
}

export function Plate({
  title,
  importPath,
  note,
  children,
  className,
  bodyClassName,
}: PlateProps) {
  return (
    <figure
      className={cx(
        "border-secondary bg-primary flex flex-col overflow-hidden rounded-xl border shadow-xs",
        className,
      )}
    >
      <figcaption className="border-secondary flex flex-col gap-0.5 border-b px-6 py-4">
        <span className="text-primary text-sm font-semibold">{title}</span>
        {note && <span className="text-tertiary text-sm">{note}</span>}
        {importPath && (
          <code className="text-quaternary font-mono text-xs">
            {importPath}
          </code>
        )}
      </figcaption>
      <div className={cx("flex flex-col gap-6 p-6", bodyClassName)}>
        {children}
      </div>
    </figure>
  )
}

interface HouseRuleProps {
  name: string
  children: ReactNode
}

export function HouseRule({ name, children }: HouseRuleProps) {
  return (
    <div className="bg-secondary flex flex-col gap-1 rounded-lg p-5">
      <h3 className="text-primary text-md font-semibold">{name}</h3>
      <p className="text-secondary max-w-prose text-sm">{children}</p>
    </div>
  )
}

interface SwatchProps {
  name: string
  hex: string
  token: string
  role: string
  swatchClassName: string
  retired?: boolean
}

export function Swatch({
  name,
  hex,
  token,
  role,
  swatchClassName,
  retired = false,
}: SwatchProps) {
  return (
    <div className="flex min-w-0 flex-col gap-2.5">
      <div
        aria-hidden="true"
        className={cx(
          "relative h-20 rounded-lg ring-1 ring-black/5 ring-inset",
          swatchClassName,
        )}
      >
        {retired && (
          <span className="bg-batyr-red-pencil absolute top-1/2 right-2 left-2 h-0.5 -rotate-3 rounded-full" />
        )}
      </div>
      <div className="flex flex-col gap-0.5">
        <span
          className={cx(
            "text-primary text-sm font-semibold",
            retired && "line-through decoration-2",
          )}
        >
          {name}
        </span>
        <span className="text-tertiary text-xs">{role}</span>
        <code className="text-quaternary font-mono text-xs">
          {hex} · {token}
        </code>
      </div>
    </div>
  )
}
